<script lang="ts">
  /**
   * BlogCard - Card component for displaying blog post previews.
   *
   * Follows the design system with CornerFrame-style corners,
   * dark card backgrounds, and proper typography hierarchy.
   */
  import Star from './Star.svelte';
  import { isDev } from '$lib/config';

  interface Props {
    title: string;
    description: string;
    date: string;
    slug: string;
    image?: string;
    tags?: string[];
    featured?: boolean;
    draft?: boolean;
  }

  let {
    title,
    description,
    date,
    slug,
    image,
    tags = [],
    featured = false,
    draft = false
  }: Props = $props();

  // Format date for display
  const formattedDate = $derived(
    new Date(date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    })
  );
</script>

<a
  href="/news/{slug}"
  class="group block bg-[#171719] border border-[#333335] relative transition-all duration-300 hover:border-[#ee6018]/50 {featured ? 'md:col-span-2 lg:col-span-2' : ''}"
>
  <!-- Quarter stars at corners -->
  <Star variant="bottom-right" class="absolute -left-px -top-px opacity-50 group-hover:opacity-100 transition-opacity" />
  <Star variant="bottom-left" class="absolute -right-px -top-px opacity-50 group-hover:opacity-100 transition-opacity" />
  <Star variant="top-right" class="absolute -left-px -bottom-px opacity-50 group-hover:opacity-100 transition-opacity" />
  <Star variant="top-left" class="absolute -right-px -bottom-px opacity-50 group-hover:opacity-100 transition-opacity" />

  <!-- Draft badge (dev only) -->
  {#if draft && isDev}
    <div class="absolute top-3 right-3 z-10 bg-yellow-500 text-black font-mono text-[10px] font-bold uppercase tracking-wider px-2 py-1">
      Draft
    </div>
  {/if}

  <!-- Image (optional - no placeholder shown when missing) -->
  {#if image}
    <div class="{featured ? 'aspect-[3/1]' : 'aspect-[16/9]'} overflow-hidden border-b border-[#333335]">
      <img
        src={image}
        alt={title}
        class="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
      />
    </div>
  {/if}

  <!-- Content -->
  <div class="p-6 {featured ? 'lg:p-8' : ''}">
    <!-- Tags -->
    {#if tags.length > 0}
      <div class="flex flex-wrap gap-2 mb-3">
        {#each tags.slice(0, 2) as tag}
          <span class="font-mono text-[11px] text-[#ee6018] uppercase tracking-wide">
            {tag}
          </span>
        {/each}
      </div>
    {/if}

    <!-- Title -->
    <h3 class="font-display text-[18px] {featured ? 'lg:text-[24px]' : ''} text-white leading-tight mb-3 group-hover:text-[#ee6018] transition-colors">
      {title}
    </h3>

    <!-- Description -->
    <p class="font-body text-[14px] text-[#878787] leading-relaxed mb-4 line-clamp-2 {featured ? 'lg:line-clamp-3' : ''}">
      {description}
    </p>

    <!-- Footer -->
    <div class="flex items-center justify-between pt-4 border-t border-[#333335]/50">
      <span class="font-mono text-[12px] text-[#5C5855]">
        {formattedDate}
      </span>
      <span class="font-mono text-[12px] text-[#ee6018] flex items-center gap-1 group-hover:gap-2 transition-all">
        Read
        <svg width="12" height="12" viewBox="0 0 12 12" fill="none" class="transition-transform group-hover:translate-x-0.5">
          <path d="M4 3L7 6L4 9" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
      </span>
    </div>
  </div>
</a>
